// src/components/pages/dialogs/AppointmentDetailDialog.tsx

import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { RawAppointment, TipoPago } from '@/components/types/typeCalendar';
import MoveAppointmentDialog from '@/components/pages/dialogs/MoveAppointmentDialog';
import PaymentTypeDialog from '@/components/pages/dialogs/PaymentTypeDialog';
import CancellationReasonDialog from '@/components/pages/dialogs/CancellationReasonDialog';
import AnulationReasonDialog from '@/components/pages/dialogs/AnulationReasonDialog';
import ReprogramacionTooltip from '@/components/pages/shared/ReprogramacionTooltip';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Pencil,
  X,
  Check,
  ChevronLeft,
  ArrowRight,
  User,
  Briefcase,
} from 'lucide-react';
import { reabrirCita, editarCita } from '@/components/hooks/useCitas';
import { useServiciosPorEmpresa } from '@/components/hooks/useServicios';

// Duración real de la cita (hora_fin - hora_cita); si no es válida, la del
// servicio. Así un servicio por horas conserva las horas reservadas.
const duracionReal = (
  horaCita: string | null | undefined,
  horaFin: string | null | undefined,
  duracionServicio: number
): number => {
  if (!horaCita || !horaFin) return duracionServicio;
  const [h1, m1] = horaCita.split(':').map(Number);
  const [h2, m2] = horaFin.split(':').map(Number);
  const diff = h2 * 60 + m2 - (h1 * 60 + m1);
  if (Number.isNaN(diff) || diff === 0) return duracionServicio;
  return diff > 0 ? diff : diff + 1440;
};


const TIPOS_PAGO: { value: TipoPago; label: string }[] = [
  { value: 'efectivo', label: 'Efectivo' },
  { value: 'tarjeta', label: 'Tarjeta' },
  { value: 'bizum', label: 'Bizum' },
  { value: 'otros', label: 'Otros' },
];

const addMinutesToTime = (time: string, minutes: number): string => {
  const [h, m] = time.split(':').map(Number);
  const total = h * 60 + m + minutes;
  return `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(
    total % 60
  ).padStart(2, '0')}`;
};

const formatDuracion = (durMin: number): string | null => {
  if (!durMin) return null;
  const h = Math.floor(durMin / 60);
  const m = durMin % 60;
  return (
    [h > 0 && `${h}h`, m > 0 && `${m}min`].filter(Boolean).join(' ') || null
  );
};

const formatHHMM = (timeStr?: string): string => {
  if (!timeStr) return '';
  const [h = '', m = ''] = timeStr.split(':');
  return `${h.padStart(2, '0')}:${m.padStart(2, '0')}`;
};

const formatFechaCorta = (fecha: string) =>
  new Date(`${fecha}T00:00:00`).toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'short',
  });

const formatFechaCreacion = (fechaISO: string) =>
  new Date(fechaISO).toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

// ── DialogTopBar (con botón atrás opcional para subvistas tipo historial) ──
const DialogTopBar = ({
  onClose,
  onBack,
}: {
  onClose: () => void;
  onBack?: () => void;
}) => (
  <>
    {onBack && (
      <button
        onClick={onBack}
        className="absolute top-2.5 left-3 z-10 shrink-0 w-9 h-9 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-800 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
        aria-label="Volver"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
    )}
    <button
      onClick={onClose}
      className="absolute top-2.5 right-3 z-10 shrink-0 w-9 h-9 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-800 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
      aria-label="Cerrar"
    >
      <X className="w-5 h-5" />
    </button>
  </>
);

// ── InlineEditRow ─────────────────────────────────────────────
interface InlineEditRowProps {
  label: string;
  displayValue: React.ReactNode;
  canEdit?: boolean;
  editContent: React.ReactNode;
  onSave: () => Promise<void>;
  onCancel: () => void;
  isEditing: boolean;
  onStartEdit: () => void;
  saving?: boolean;
  alwaysShow?: boolean;
}

function InlineEditRow({
  label,
  displayValue,
  canEdit = true,
  editContent,
  onSave,
  onCancel,
  isEditing,
  onStartEdit,
  saving = false,
  alwaysShow = false,
}: InlineEditRowProps) {
  const empty =
    displayValue === null || displayValue === undefined || displayValue === '';
  if (!isEditing && !alwaysShow && empty) return null;

  return (
    <div className="flex items-center gap-1.5 text-sm sm:text-xs min-h-[26px] sm:min-h-[22px]">
      <span className="text-gray-400 dark:text-gray-500 w-24 flex-shrink-0">
        {label}:
      </span>
      <div className="flex-1 flex items-center gap-1 min-w-0">
        {isEditing ? (
          <>
            <div className="flex-1 min-w-0">{editContent}</div>
            <button
              onClick={onSave}
              disabled={saving}
              title="Guardar"
              className="flex-shrink-0 h-6 w-6 sm:h-5 sm:w-5 rounded-full flex items-center justify-center text-[#103254] dark:text-blue-300 hover:bg-gray-100 dark:hover:bg-white/10 disabled:opacity-50"
            >
              {saving ? (
                <span className="text-xs sm:text-[10px]">…</span>
              ) : (
                <Check className="w-3.5 h-3.5 sm:w-3 sm:h-3" />
              )}
            </button>
            <button
              onClick={onCancel}
              disabled={saving}
              title="Cancelar"
              className="flex-shrink-0 h-6 w-6 sm:h-5 sm:w-5 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/10 disabled:opacity-50"
            >
              <X className="w-3.5 h-3.5 sm:w-3 sm:h-3" />
            </button>
          </>
        ) : (
          <>
            <span className="font-medium text-gray-800 dark:text-white break-words flex-1 text-sm sm:text-xs">
              {empty ? '—' : displayValue}
            </span>
            {canEdit && (
              <button
                onClick={onStartEdit}
                title="Editar"
                className="flex-shrink-0 h-6 w-6 sm:h-5 sm:w-5 rounded-full flex items-center justify-center text-gray-400 dark:text-gray-500 hover:text-[#103254] dark:hover:text-blue-300 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
              >
                <Pencil className="w-3 h-3 sm:w-2.5 sm:h-2.5" />
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function DetailRow({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="flex items-center gap-1.5 text-sm sm:text-xs min-h-[26px] sm:min-h-[22px]">
      <span className="text-gray-400 dark:text-gray-500 w-24 flex-shrink-0">
        {label}:
      </span>
      <span className="font-medium text-gray-800 dark:text-white break-words flex-1">
        {value}
      </span>
    </div>
  );
}

function RatingStars({ value, size = 16 }: { value: number; size?: number }) {
  const rounded = Math.round(value || 0);
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <span
          key={n}
          className={
            n <= rounded
              ? 'text-yellow-500'
              : 'text-gray-300 dark:text-gray-600'
          }
          style={{ fontSize: size }}
        >
          {n <= rounded ? '★' : '☆'}
        </span>
      ))}
    </div>
  );
}

type EditingField =
  | 'servicio'
  | 'nombre'
  | 'telefono'
  | 'email'
  | 'tipo_pago'
  | null;

interface Props {
  cita: RawAppointment | null;
  onClose: () => void;
  onCancelar: () => void;
  onCompletar: () => void;
  onReabrir: () => void;
  onAnular: () => void;
  incrementReload: () => void;
}

const AppointmentDetailDialog: React.FC<Props> = ({
  cita,
  onClose,
  onCancelar,
  onCompletar,
  onReabrir,
  onAnular,
  incrementReload,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [completarDialogOpen, setCompletarDialogOpen] = useState(false);
  const [tipoPagoCompletar, setTipoPagoCompletar] = useState('tarjeta');
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [anularDialogOpen, setAnularDialogOpen] = useState(false);
  const [historialOpen, setHistorialOpen] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [editingField, setEditingField] = useState<EditingField>(null);
  const [saving, setSaving] = useState(false);
  const [tempServicio, setTempServicio] = useState('');
  const [tempHoraFin, setTempHoraFin] = useState('');
  const [tempDuracion, setTempDuracion] = useState(0);
  const [tempNombre, setTempNombre] = useState('');
  const [tempTelefono, setTempTelefono] = useState('');
  const [tempEmail, setTempEmail] = useState('');
  const [tempTipoPago, setTempTipoPago] = useState<TipoPago | ''>('');

  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 640px)');
    setIsMobile(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const idEmpresa = localStorage.getItem('id_empresa') || '';
  const { servicios } = useServiciosPorEmpresa(idEmpresa);

  if (!cita) return null;

  const estado = cita.estado_cita.toLowerCase();
  const puedeEditarCita = !['cancelado', 'anulado'].includes(estado);

  const historial = cita.historial_reprogramaciones ?? [];
  const ultimaReprogramacion = historial[0] ?? null;

  // Duración reservada (servicios por horas, citas alargadas); en citas
  // desbordadas de varios días se mantiene la del servicio.
  const duracionCita = cita.desbordamiento
    ? cita.duracion_minutos ?? 0
    : duracionReal(cita.hora_cita, cita.hora_fin, cita.duracion_minutos ?? 0);
  const duracionMostrar =
    editingField === 'servicio' ? tempDuracion : duracionCita;
  const horaFinMostrar =
    editingField === 'servicio' ? tempHoraFin : cita.hora_fin ?? '';
  const duracionLegible = useMemo(
    () => formatDuracion(duracionMostrar),
    [duracionMostrar]
  );

  const servicioNombreMostrar = useMemo(() => {
    if (editingField !== 'servicio') return cita.nombre_servicio;
    if (!tempServicio) return '—';
    return (
      servicios.find((s) => s.id_servicio === tempServicio)?.servicio ?? '—'
    );
  }, [editingField, tempServicio, servicios, cita.nombre_servicio]);

  const franjaHoraria = useMemo(() => {
    const hi = formatHHMM(cita.hora_cita);
    const hf = formatHHMM(horaFinMostrar);
    if (hi && hf) return `${hi} – ${hf}`;
    if (hi) return hi;
    return '';
  }, [cita.hora_cita, horaFinMostrar]);

  const startEdit = (field: EditingField) => {
    if (editingField) return;
    setEditingField(field);
    if (field === 'servicio') {
      setTempServicio(cita.id_servicio || '');
      setTempDuracion(duracionCita);
      setTempHoraFin(cita.hora_fin || '');
    } else if (field === 'nombre') setTempNombre(cita.nombre_cliente || '');
    else if (field === 'telefono') setTempTelefono(cita.telefono || '');
    else if (field === 'email')
      setTempEmail(cita.email_contacto || cita.email || '');
    else if (field === 'tipo_pago') setTempTipoPago(cita.tipo_pago ?? '');
  };

  const cancelEdit = () => setEditingField(null);

  const saveField = async (field: EditingField) => {
    setSaving(true);
    const payloads: Record<string, Parameters<typeof editarCita>[1]> = {
      servicio: {
        id_servicio: tempServicio || undefined,
        hora_fin: tempHoraFin || undefined,
      },
      nombre: { nombre: tempNombre || undefined },
      telefono: { telefono: tempTelefono || undefined },
      email: { email: tempEmail || undefined },
      tipo_pago: { tipo_pago: tempTipoPago || undefined },
    };
    const resp = await editarCita(cita.id_cita, payloads[field!] ?? {});
    setSaving(false);
    if (resp.ok) {
      toast.success('Cita actualizada correctamente');
      setEditingField(null);
      incrementReload();
      onClose();
    } else {
      toast.error('Error al actualizar la cita');
    }
  };

  const handleReabrir = async () => {
    setLoading(true);
    const resp = await reabrirCita(cita.id_cita);
    setLoading(false);
    if (resp.ok) {
      toast.success(t('appointmentDetailDialog.successReopen'));
      onReabrir();
      incrementReload();
    } else {
      toast.error(t('appointmentDetailDialog.errorReopen'));
    }
  };

  const inputCls =
    'h-7 sm:h-6 rounded border border-gray-300 dark:border-white/10 bg-white dark:bg-[#1c1c1e] text-gray-900 dark:text-white text-sm sm:text-xs px-2 focus:outline-none focus:ring-1 focus:ring-[#103254] dark:focus:ring-blue-300 w-full';

  const estadoColor: Record<string, string> = {
    confirmado: 'bg-blue-600',
    completado: 'bg-green-600',
    cancelado: 'bg-red-600',
    anulado: 'bg-yellow-500',
  };

  const textoUltimaReprogramacion = ultimaReprogramacion ? (
    <span className="font-medium text-gray-500 dark:text-gray-400 line-through break-words">
      {formatFechaCorta(ultimaReprogramacion.fecha_anterior)}{' '}
      {formatHHMM(ultimaReprogramacion.hora_anterior)}
    </span>
  ) : null;

  const mostrandoSubvista =
    moveDialogOpen ||
    completarDialogOpen ||
    cancelDialogOpen ||
    anularDialogOpen ||
    historialOpen;

  return (
    <Dialog open={!!cita} onOpenChange={onClose}>
      <DialogContent
        className="
          z-[60] p-0 border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1c1c1e]
          [&>button:last-child]:hidden
          flex flex-col
          !bottom-0 !left-0 !top-auto !translate-x-0 !translate-y-0
          w-full max-w-none h-auto max-h-[85vh] rounded-t-2xl rounded-b-none
          data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom
          data-[state=open]:duration-300 data-[state=closed]:duration-200
          data-[state=open]:ease-out data-[state=closed]:ease-in
          sm:!bottom-auto sm:!left-1/2 sm:!top-1/2 sm:!-translate-x-1/2 sm:!-translate-y-1/2
          sm:w-[95%] sm:max-w-md sm:mx-auto sm:h-auto sm:max-h-[85vh] sm:rounded-xl sm:shadow-xl
          sm:data-[state=open]:zoom-in-95 sm:data-[state=closed]:zoom-out-95
          sm:data-[state=open]:slide-in-from-bottom-0 sm:data-[state=closed]:slide-out-to-bottom-0
        "
      >
        <DialogTopBar
          onClose={onClose}
          onBack={historialOpen ? () => setHistorialOpen(false) : undefined}
        />

        <DialogHeader className="px-5 pt-4 pb-1 pr-14 pl-14 shrink-0">
          <DialogTitle className="text-sm font-semibold text-[#103254] dark:text-blue-300 text-center">
            {historialOpen
              ? 'Historial de cambios'
              : moveDialogOpen
              ? t('appointmentDetailDialog.titleMove')
              : completarDialogOpen
              ? t('appointmentDetailDialog.titlePayment', 'Completar cita')
              : cancelDialogOpen
              ? t('cancellationReasonDialog.defaultTitle')
              : anularDialogOpen
              ? t('anulationReasonDialog.defaultTitle')
              : t('appointmentDetailDialog.titleDetail')}
          </DialogTitle>
          {!historialOpen && (
            <div
              className={`mt-1.5 h-0.5 w-full rounded-full ${
                estadoColor[estado] ?? 'bg-gray-400'
              }`}
            />
          )}
        </DialogHeader>

        <div className="px-5 py-3 flex-1 overflow-y-auto">
          {historialOpen ? (
            <div className="space-y-3">
              {historial.map((mov, idx) => {
                const esCliente = mov.reprogramado_por === 'cliente';
                const esMasReciente = idx === 0;
                return (
                  <div
                    key={`${mov.fecha_creacion}-${idx}`}
                    className={`rounded-xl border p-3.5 transition-colors ${
                      esMasReciente
                        ? 'border-[#103254]/20 bg-[#103254]/[0.03] dark:border-blue-800/40 dark:bg-blue-900/10'
                        : 'border-gray-100 dark:border-white/10'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2.5">
                      <span
                        className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-1 rounded-full border ${
                          esCliente
                            ? 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800'
                            : 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/20 dark:text-purple-400 dark:border-purple-800'
                        }`}
                      >
                        {esCliente ? (
                          <User className="w-3 h-3" />
                        ) : (
                          <Briefcase className="w-3 h-3" />
                        )}
                        {esCliente ? 'Cliente' : 'Trabajador'}
                      </span>
                      <span className="text-[11px] text-gray-400 dark:text-gray-500">
                        {formatFechaCreacion(mov.fecha_creacion)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-0.5">
                          Antes
                        </p>
                        <p className="font-medium text-gray-500 dark:text-gray-400 line-through truncate">
                          {formatFechaCorta(mov.fecha_anterior)} ·{' '}
                          {formatHHMM(mov.hora_anterior)}
                        </p>
                      </div>
                      <ArrowRight className="w-3.5 h-3.5 text-gray-300 dark:text-gray-600 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-0.5">
                          Después
                        </p>
                        <p className="font-semibold text-gray-800 dark:text-gray-100 truncate">
                          {formatFechaCorta(mov.fecha_nueva)} ·{' '}
                          {formatHHMM(mov.hora_nueva)}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : !mostrandoSubvista ? (
            <>
              <div className="rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 shadow-sm p-3 space-y-1.5">
                <DetailRow
                  label={t('appointmentDetailDialog.labelWorker')}
                  value={
                    cita.trabajadores_en_cita?.length
                      ? cita.trabajadores_en_cita
                          .map((tr) => tr.nombre)
                          .join(', ')
                      : '-'
                  }
                />

                <InlineEditRow
                  label={t('appointmentDetailDialog.labelService')}
                  displayValue={servicioNombreMostrar}
                  canEdit={puedeEditarCita}
                  isEditing={editingField === 'servicio'}
                  onStartEdit={() => startEdit('servicio')}
                  onCancel={cancelEdit}
                  onSave={() => saveField('servicio')}
                  saving={saving}
                  alwaysShow
                  editContent={
                    <Select
                      value={tempServicio}
                      onValueChange={(newId) => {
                        const svc = servicios.find(
                          (s) => s.id_servicio === newId
                        );
                        const durMin = svc
                          ? svc.reserva_por_horas
                            ? duracionCita
                            : Number(svc.duracion_minutos)
                          : 0;
                        setTempServicio(newId);
                        setTempDuracion(durMin);
                        setTempHoraFin(
                          durMin > 0 && cita.hora_cita
                            ? addMinutesToTime(cita.hora_cita, durMin)
                            : ''
                        );
                      }}
                    >
                      <SelectTrigger className="h-7 sm:h-6 text-sm sm:text-xs dark:bg-white/5 dark:border-white/10 dark:text-white">
                        <SelectValue placeholder="Sin servicio" />
                      </SelectTrigger>
                      <SelectContent className="z-[70]">
                        {servicios.map((s) => (
                          <SelectItem
                            key={s.id_servicio}
                            value={s.id_servicio}
                            className="text-sm sm:text-xs"
                          >
                            {s.servicio}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  }
                />

                {duracionLegible && (
                  <DetailRow
                    label={t('appointmentDetailDialog.labelDuration')}
                    value={duracionLegible}
                  />
                )}

                <DetailRow
                  label={t('appointmentDetailDialog.labelDate')}
                  value={
                    cita.fecha_fin && cita.fecha_fin !== cita.fecha_cita
                      ? `${cita.fecha_cita} → ${cita.fecha_fin}`
                      : cita.fecha_cita
                  }
                />

                <DetailRow
                  label={t('appointmentDetailDialog.labelStartTime')}
                  value={franjaHoraria}
                />

                {ultimaReprogramacion && (
                  <div className="flex items-center gap-1.5 text-sm sm:text-xs min-h-[26px] sm:min-h-[22px]">
                    <span className="text-gray-400 dark:text-gray-500 w-24 flex-shrink-0">
                      {t(
                        'appointmentDetailDialog.labelPreviousDate',
                        'Antes era'
                      )}
                      :
                    </span>
                    <div className="flex-1 flex items-center gap-1.5 flex-wrap min-w-0">
                      {isMobile ? (
                        <>
                          {textoUltimaReprogramacion}
                          {historial.length > 1 && (
                            <button
                              onClick={() => setHistorialOpen(true)}
                              className="text-xs sm:text-[11px] font-semibold text-[#103254] dark:text-blue-300 underline underline-offset-2 flex-shrink-0"
                            >
                              {t(
                                'appointmentDetailDialog.btnViewHistory',
                                'Ver historial'
                              )}
                            </button>
                          )}
                        </>
                      ) : (
                        <ReprogramacionTooltip historial={historial}>
                          <span className="cursor-help">
                            {textoUltimaReprogramacion}
                          </span>
                        </ReprogramacionTooltip>
                      )}
                    </div>
                  </div>
                )}

                <DetailRow
                  label={t('appointmentDetailDialog.labelStatus')}
                  value={cita.estado_cita}
                />

                <InlineEditRow
                  label={t('appointmentDetailDialog.labelClient')}
                  displayValue={cita.nombre_cliente}
                  isEditing={editingField === 'nombre'}
                  onStartEdit={() => startEdit('nombre')}
                  onCancel={cancelEdit}
                  onSave={() => saveField('nombre')}
                  saving={saving}
                  alwaysShow
                  editContent={
                    <Input
                      value={tempNombre}
                      autoFocus
                      onChange={(e) => setTempNombre(e.target.value)}
                      placeholder="Nombre"
                      className={inputCls}
                    />
                  }
                />

                <InlineEditRow
                  label={t('appointmentDetailDialog.labelPhone')}
                  displayValue={
                    cita.telefono ? (
                      <span className="flex items-center gap-1.5">
                        {cita.telefono}
                        <button
                          onClick={() => {
                            const msg = encodeURIComponent(
                              `Hola, te contactamos desde ${cita.nombre_empresa}`
                            );
                            window.open(
                              `https://wa.me/${cita.telefono}?text=${msg}`,
                              '_blank'
                            );
                          }}
                          className="shrink-0 opacity-70 hover:opacity-100 transition-opacity"
                          title="Abrir WhatsApp"
                        >
                          <svg
                            viewBox="0 0 24 24"
                            className="w-4 h-4 fill-green-500"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                            <path d="M12 0C5.373 0 0 5.373 0 12c0 2.124.554 4.118 1.525 5.847L.057 23.986l6.305-1.654A11.954 11.954 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.882a9.88 9.88 0 01-5.034-1.376l-.361-.214-3.741.981.999-3.648-.235-.374A9.859 9.859 0 012.118 12C2.118 6.534 6.534 2.118 12 2.118c5.465 0 9.882 4.416 9.882 9.882 0 5.465-4.417 9.882-9.882 9.882z" />
                          </svg>
                        </button>
                      </span>
                    ) : null
                  }
                  isEditing={editingField === 'telefono'}
                  onStartEdit={() => startEdit('telefono')}
                  onCancel={cancelEdit}
                  onSave={() => saveField('telefono')}
                  saving={saving}
                  alwaysShow
                  editContent={
                    <Input
                      value={tempTelefono}
                      autoFocus
                      onChange={(e) => setTempTelefono(e.target.value)}
                      placeholder="Teléfono"
                      className={inputCls}
                    />
                  }
                />

                <InlineEditRow
                  label={t('appointmentDetailDialog.labelEmail')}
                  displayValue={cita.email_contacto || cita.email}
                  isEditing={editingField === 'email'}
                  onStartEdit={() => startEdit('email')}
                  onCancel={cancelEdit}
                  onSave={() => saveField('email')}
                  saving={saving}
                  alwaysShow
                  editContent={
                    <Input
                      type="email"
                      value={tempEmail}
                      autoFocus
                      onChange={(e) => setTempEmail(e.target.value)}
                      placeholder="Email"
                      className={inputCls}
                    />
                  }
                />

                <DetailRow
                  label={t('appointmentDetailDialog.labelPrice')}
                  value={`${Number(cita.precio).toFixed(2)} €`}
                />

                {(estado === 'completado' ||
                  (['completado', 'anulado'].includes(estado) &&
                    cita.tipo_pago)) && (
                  <InlineEditRow
                    label={t('appointmentDetailDialog.labelPaymentType')}
                    displayValue={cita.tipo_pago}
                    canEdit={estado === 'completado'}
                    isEditing={editingField === 'tipo_pago'}
                    onStartEdit={() => startEdit('tipo_pago')}
                    onCancel={cancelEdit}
                    onSave={() => saveField('tipo_pago')}
                    saving={saving}
                    alwaysShow={estado === 'completado'}
                    editContent={
                      <Select
                        value={tempTipoPago}
                        onValueChange={(v) =>
                          setTempTipoPago(v as TipoPago | '')
                        }
                      >
                        <SelectTrigger className="h-7 sm:h-6 text-sm sm:text-xs dark:bg-white/5 dark:border-white/10 dark:text-white">
                          <SelectValue placeholder="Sin tipo de pago" />
                        </SelectTrigger>
                        <SelectContent className="z-[70]">
                          {TIPOS_PAGO.map((tp) => (
                            <SelectItem
                              key={tp.value}
                              value={tp.value}
                              className="text-sm sm:text-xs"
                            >
                              {tp.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    }
                  />
                )}

                <DetailRow
                  label={t('appointmentDetailDialog.labelNotes')}
                  value={cita.observaciones}
                />
                <DetailRow
                  label={t('appointmentDetailDialog.labelCancelReason')}
                  value={cita.motivo_cancelacion}
                />
                {estado === 'cancelado' && cita.fecha_cancelacion && (
                  <DetailRow
                    label={t('appointmentDetailDialog.labelCancelDate')}
                    value={cita.fecha_cancelacion}
                  />
                )}
                <DetailRow
                  label={t('appointmentDetailDialog.labelAnulReason')}
                  value={cita.motivo_anulado}
                />
                {estado === 'anulado' && cita.fecha_anulado && (
                  <DetailRow
                    label={t('appointmentDetailDialog.labelAnulDate')}
                    value={cita.fecha_anulado}
                  />
                )}
                {cita.num_clientes > 1 && (
                  <DetailRow
                    label={t('appointmentDetailDialog.labelParticipants')}
                    value={cita.num_clientes}
                  />
                )}
                {estado === 'completado' && cita.fecha_completado && (
                  <DetailRow
                    label={t('appointmentDetailDialog.labelCompletedDate')}
                    value={cita.fecha_completado}
                  />
                )}
              </div>

              {cita.tiene_valoracion && (
                <div className="mt-2 p-2.5 rounded-lg bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm sm:text-xs font-semibold text-gray-800 dark:text-white">
                      {t('appointmentDetailDialog.ratingTitle')}
                    </span>
                    {cita.puntuacion_cita != null && (
                      <RatingStars value={cita.puntuacion_cita} size={14} />
                    )}
                  </div>
                  {cita.comentario_valoracion && (
                    <p className="text-sm sm:text-xs text-gray-600 dark:text-gray-400 italic">
                      "{cita.comentario_valoracion}"
                    </p>
                  )}
                </div>
              )}
            </>
          ) : moveDialogOpen ? (
            <MoveAppointmentDialog
              open={moveDialogOpen}
              onClose={() => setMoveDialogOpen(false)}
              onSuccess={() => {
                setMoveDialogOpen(false);
                onClose();
                incrementReload();
              }}
              citaId={cita.id_cita}
              cita={cita}
              initialDate={cita.fecha_cita}
              initialTime={cita.hora_cita}
              initialObservaciones={cita.observaciones || ''}
              initialHoraFin={cita.hora_fin || ''}
              duracionMinutos={duracionReal(
                cita.hora_cita,
                cita.hora_fin,
                cita.duracion_minutos
              )}
            />
          ) : completarDialogOpen ? (
            <PaymentTypeDialog
              idCita={cita.id_cita}
              emailCliente={cita.email_contacto || cita.email || ''}
              idServicio={cita.id_servicio}
              tipoPago={tipoPagoCompletar}
              setTipoPago={setTipoPagoCompletar}
              precio={Number(cita.precio)}
              onCancel={() => setCompletarDialogOpen(false)}
              onCloseAll={() => {
                setCompletarDialogOpen(false);
                onClose();
                onCompletar();
              }}
              incrementReload={incrementReload}
            />
          ) : cancelDialogOpen ? (
            <CancellationReasonDialog
              idCita={cita.id_cita}
              motivo={motivo}
              setMotivo={setMotivo}
              open={cancelDialogOpen}
              onCancel={() => setCancelDialogOpen(false)}
              onCloseAll={() => {
                setCancelDialogOpen(false);
                onClose();
                onCancelar();
              }}
              incrementReload={incrementReload}
            />
          ) : (
            <AnulationReasonDialog
              idCita={cita.id_cita}
              motivo={motivo}
              setMotivo={setMotivo}
              open={anularDialogOpen}
              onCancel={() => setAnularDialogOpen(false)}
              onCloseAll={() => {
                setAnularDialogOpen(false);
                onClose();
                onAnular();
              }}
              incrementReload={incrementReload}
            />
          )}
        </div>

        {!mostrandoSubvista && (
          <DialogFooter className="px-5 pt-2 pb-5 sm:pb-4 flex flex-col sm:flex-row sm:justify-between gap-2 shrink-0 border-t border-gray-100 dark:border-white/10">
            {estado === 'confirmado' && (
              <Button
                variant="outline"
                onClick={() => setMoveDialogOpen(true)}
                className="rounded-md text-sm sm:text-xs h-9 sm:h-8 font-medium text-[#103254] dark:text-blue-300 bg-transparent dark:bg-transparent border-[#103254] dark:border-blue-300 hover:bg-gray-50 dark:hover:bg-white/10 w-full sm:w-auto"
              >
                {t('appointmentDetailDialog.btnMove')}
              </Button>
            )}
            <div className="flex flex-col sm:flex-row gap-1.5 w-full sm:w-auto justify-end">
              {estado === 'confirmado' && (
                <>
                  <Button
                    variant="outline"
                    onClick={() => setCompletarDialogOpen(true)}
                    className="rounded-md text-sm sm:text-xs h-9 sm:h-8 font-medium text-green-600 dark:text-green-400 border border-green-600 dark:border-green-500 bg-transparent hover:bg-green-50 dark:hover:bg-green-900/20 w-full sm:w-auto"
                  >
                    {t('appointmentDetailDialog.btnComplete')}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setCancelDialogOpen(true)}
                    className="rounded-md text-sm sm:text-xs h-9 sm:h-8 font-medium text-red-600 dark:text-red-400 border border-red-600 dark:border-red-500 bg-transparent hover:bg-red-50 dark:hover:bg-red-900/20 w-full sm:w-auto"
                  >
                    {t('appointmentDetailDialog.btnCancel')}
                  </Button>
                </>
              )}
              {(estado === 'completado' ||
                ['cancelado', 'anulado'].includes(estado)) && (
                <Button
                  variant="outline"
                  onClick={handleReabrir}
                  disabled={loading}
                  className="rounded-md text-sm sm:text-xs h-9 sm:h-8 font-medium text-[#103254] dark:text-blue-300 border border-[#103254] dark:border-blue-300 bg-transparent hover:bg-gray-50 dark:hover:bg-white/10 disabled:opacity-50 w-full sm:w-auto"
                >
                  {loading
                    ? t('appointmentDetailDialog.btnReopening')
                    : t('appointmentDetailDialog.btnReopen')}
                </Button>
              )}
              {estado === 'completado' && (
                <Button
                  variant="outline"
                  onClick={() => setAnularDialogOpen(true)}
                  className="rounded-md text-sm sm:text-xs h-9 sm:h-8 font-medium text-yellow-500 dark:text-yellow-400 border border-yellow-500 dark:border-yellow-600 bg-transparent hover:bg-yellow-50 dark:hover:bg-yellow-900/20 w-full sm:w-auto"
                >
                  {t('appointmentDetailDialog.btnVoid')}
                </Button>
              )}
            </div>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AppointmentDetailDialog;